import { Vec2, arcSweep, clamp } from './math';
import { generateHatch } from './hatch';
import { dimensionGeometry } from './dimension';
import { flattenSpline } from './spline';
import type { Entity } from './types';

export interface FlattenOptions {
  /**
   * Maximum deviation between the true curve and its polyline approximation.
   * Callers derive this from screen resolution (e.g. 0.35 px / zoom).
   */
  chordTolerance: number;
  maxArcSegments?: number;
}

export interface FlatPath {
  points: Vec2[];
  closed: boolean;
}

export const DEFAULT_FLATTEN: FlattenOptions = { chordTolerance: 0.05, maxArcSegments: 720 };

/** Segments needed so the sagitta of the chord stays under `tolerance`. */
export function segmentsForArc(
  radius: number,
  sweep: number,
  tolerance: number,
  maxSegments = 720,
): number {
  const r = Math.abs(radius);
  const tol = Math.max(tolerance, 1e-9);
  if (r <= tol) return Math.max(4, Math.ceil(sweep / (Math.PI / 8)));
  const theta = 2 * Math.acos(clamp(1 - tol / r, -1, 1));
  if (!Number.isFinite(theta) || theta <= 0) return maxSegments;
  return clamp(Math.ceil(sweep / theta), 2, maxSegments);
}

export function flattenArc(
  center: Vec2,
  radius: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
  opts: FlattenOptions,
): Vec2[] {
  const sweep = arcSweep(startAngle, endAngle, ccw);
  const n = segmentsForArc(radius, sweep, opts.chordTolerance, opts.maxArcSegments ?? 720);
  const direction = ccw ? 1 : -1;
  const points: Vec2[] = [];
  for (let i = 0; i <= n; i++) {
    points.push(Vec2.polar(radius, startAngle + direction * (sweep * i) / n).add(center));
  }
  return points;
}

export function flattenCircle(
  center: Vec2,
  radius: number,
  opts: FlattenOptions,
): Vec2[] {
  const n = segmentsForArc(radius, Math.PI * 2, opts.chordTolerance, opts.maxArcSegments ?? 720);
  const points: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2;
    points.push(Vec2.polar(radius, angle).add(center));
  }
  return points;
}

export function flattenEllipse(
  center: Vec2,
  rx: number,
  ry: number,
  rotation: number,
  opts: FlattenOptions,
): Vec2[] {
  const r = Math.max(rx, ry);
  const n = segmentsForArc(r, Math.PI * 2, opts.chordTolerance, opts.maxArcSegments ?? 720);
  const c = Math.cos(rotation);
  const s = Math.sin(rotation);
  const points: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const x = Math.cos(t) * rx;
    const y = Math.sin(t) * ry;
    points.push(Vec2.of(center.x + x * c - y * s, center.y + x * s + y * c));
  }
  return points;
}

/** Converts an entity into renderable polylines. Text is rendered separately. */
export function entityPaths(entity: Entity, opts: FlattenOptions = DEFAULT_FLATTEN): FlatPath[] {
  switch (entity.type) {
    case 'line':
      return [{ points: [entity.a, entity.b], closed: false }];
    case 'polyline':
      return [{ points: entity.vertices, closed: entity.closed }];
    case 'circle':
      return [{ points: flattenCircle(entity.center, entity.radius, opts), closed: true }];
    case 'arc':
      return [
        {
          points: flattenArc(
            entity.center,
            entity.radius,
            entity.startAngle,
            entity.endAngle,
            entity.ccw,
            opts,
          ),
          closed: false,
        },
      ];
    case 'ellipse':
      return [
        {
          points: flattenEllipse(entity.center, entity.rx, entity.ry, entity.rotation, opts),
          closed: true,
        },
      ];
    case 'spline':
      return [{ points: flattenSpline(entity, opts), closed: entity.closed }];
    case 'hatch':
      return generateHatch(entity).map((points) => ({ points, closed: false }));
    case 'dimension':
      return dimensionGeometry(entity).lines.map((points) => ({ points, closed: false }));
    default:
      return [];
  }
}

/** Approximate polyline length in millimetres. */
export function polylineLength(points: readonly Vec2[], closed: boolean): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += points[i - 1]!.distanceTo(points[i]!);
  if (closed && points.length > 2) total += points[points.length - 1]!.distanceTo(points[0]!);
  return total;
}
